import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Shield, Loader2, ChevronRight, AlertTriangle, X } from "lucide-react";

import SparkleBtn from "@/components/RefClick/SparkleBtn";
import { api } from "@/lib/api";

/**
 * One-tap "check my CURRENT IP" button for the SummaryBar.
 *
 * Owner's workflow (he explained in the correction thread):
 *   > he switches proxies while doing referral work — one tap tells him
 *   > instantly if his current proxy is fraud-flagged AND whether
 *   > he's already used it.
 *
 * UX contract:
 *   1. NO text input in the bar. One Shield button + optional "My IP"
 *      label (hidden on narrow phones to stay inside the compact strip).
 *   2. Click = detect caller IP via ipify (client-side) → run the SAME
 *      /api/ipcheck/check pipeline → anchored popover opens below the
 *      button with: IP, animated score ring, badges, geo one-liner,
 *      PROMINENT duplicate alert ("seen ×N before, last Z → now X"),
 *      and a "Details" button that hands off to the full modal.
 *   3. Server fallback: if ipify fails (CSP, offline, DNS), POST with
 *      no body → backend reads X-Forwarded-For → same code path.
 *   4. Popover is dismissable (X / outside click / Esc).
 */

/**
 * Try to detect the browser's own public IP from a chain of free
 * services. The owner reported "Check failed" because adblockers were
 * blocking api.ipify.org. We iterate through a few CORS-friendly
 * alternates — any single failure doesn't surface an error. Returns
 * ``null`` only when EVERY path fails; the backend then falls back to
 * reading X-Forwarded-For.
 *
 * Each endpoint is given a tight 2.5 s timeout so a stalled DNS lookup
 * (common with privacy extensions) doesn't block the whole chain.
 */
async function fetchClientIp() {
  const endpoints = [
    // Each entry = {url, parse(body) → ip?}
    { url: "https://api.ipify.org?format=json",   parse: (j) => (typeof j === "object" ? j.ip : null) },
    { url: "https://api64.ipify.org?format=json", parse: (j) => (typeof j === "object" ? j.ip : null) },
    { url: "https://ipv4.icanhazip.com",          parse: (t) => (typeof t === "string" ? t.trim() : null) },
    { url: "https://api.ipify.org",               parse: (t) => (typeof t === "string" ? t.trim() : null) },
    { url: "https://ifconfig.me/ip",              parse: (t) => (typeof t === "string" ? t.trim() : null) },
  ];
  for (const { url, parse } of endpoints) {
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 2500);
      const r = await fetch(url, { cache: "no-store", signal: ctl.signal });
      clearTimeout(timer);
      if (!r.ok) continue;
      const ct = r.headers.get("content-type") || "";
      const body = ct.includes("application/json") ? await r.json() : await r.text();
      const ip = parse(body);
      if (ip && /^[\da-f.:]{3,}$/i.test(ip)) return ip;
    } catch {
      // network / CORS / timeout — try the next source silently.
    }
  }
  return null;
}

/** Score 0-100 → smooth green→yellow→red hue (shared with IpCheckModal). */
function scoreColor(score) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const hue = s < 50 ? 140 - (s / 50) * 90 : 50 - ((s - 50) / 50) * 50;
  return `hsl(${hue}, 85%, 55%)`;
}

/** Mini score ring — single file, no dep on IpCheckModal. */
function PopoverRing({ score, size = 44 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const [shown, setShown] = useState(0);
  // One-shot ease on mount.
  useState(() => {
    const start = performance.now();
    const dur = 650;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setShown(score * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    return 0;
  });
  const dash = (Math.max(0, Math.min(100, shown)) / 100) * c;
  const col = scoreColor(shown);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgb(255 255 255 / 0.08)" strokeWidth={3.5} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={col}
          strokeWidth={3.5}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          fill="none"
          style={{ filter: `drop-shadow(0 0 5px ${col})` }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-display text-xs font-bold tabular-nums"
          style={{ color: col }}
        >
          {Math.round(shown)}
        </span>
      </div>
    </div>
  );
}

/** Compact tinted badge chip. */
function BadgeChip({ b }) {
  const cls =
    b === "tor"          ? "border-rose-500/40 bg-rose-500/15 text-rose-300" :
    b === "vpn"          ? "border-amber-500/40 bg-amber-500/15 text-amber-300" :
    b === "proxy"        ? "border-amber-500/40 bg-amber-500/15 text-amber-300" :
    b === "datacenter"   ? "border-sky-500/40 bg-sky-500/15 text-sky-300" :
    b === "mobile"       ? "border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-300" :
    b === "residential"  ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" :
    b === "mixed-signals" ? "border-yellow-500/40 bg-yellow-500/15 text-yellow-300" :
                            "border-white/15 bg-white/10 text-text-main";
  return (
    <span className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {b.replace("-", " ")}
    </span>
  );
}

export default function QuickIpCheck({ onOpenModal }) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {ip, combined_score, badges, geo, previous_checks}
  const [error, setError] = useState(null);
  const anchorRef = useRef(null);

  const run = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // 1. Try the browser-side IP detection chain (ipify → icanhazip
      //    → ifconfig.me). ALL of these failing is normal when an
      //    adblocker is active — in that case we fall through to the
      //    server-side X-Forwarded-For extraction.
      const ip = await fetchClientIp();
      // 2. If every browser-side source failed, send an empty body
      //    so the server reads XFF / x-real-ip / cf-connecting-ip.
      const r = await api.ipcheck.check(ip || undefined);
      setResult(r);
    } catch (e) {
      // Server-side extraction failed too (no public IP in any proxy
      // header). Surface a helpful, specific message instead of the
      // generic "Check failed" — gives the user an obvious next step.
      const detail = e?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail
          ? detail.code
          : null;
      if (code === "no_client_ip") {
        setError({
          code: "no_client_ip",
          msg: "We couldn't detect your IP from your browser or the request headers. Open the full checker and type it in manually.",
        });
      } else {
        const msg =
          (typeof detail === "object" ? detail.message : detail) ||
          e?.message ||
          "Check failed";
        setError({ code: "generic", msg });
      }
    } finally {
      setRunning(false);
    }
  }, []);

  const handleClick = useCallback(() => {
    // Toggle: second click when popover is already open just closes it.
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    run();
  }, [open, run]);

  // Close on Esc anywhere on the page while the popover is open.
  useState(() => {
    if (typeof window === "undefined") return 0;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const scoreCol = result ? scoreColor(result.combined_score) : "white";
  const dup = result?.previous_checks;

  return (
    <div className="relative" ref={anchorRef}>
      <SparkleBtn
        type="button"
        onClick={handleClick}
        data-testid="quick-ipcheck-btn"
        aria-label="Check my current IP for fraud"
        title="Check my current IP"
        className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 text-text-main transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent sm:h-8 sm:px-2.5"
      >
        {running ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Shield className="h-3.5 w-3.5" />
        )}
        <span className="hidden text-[11px] font-medium sm:inline">My IP</span>
      </SparkleBtn>

      <AnimatePresence>
        {open && (
          <>
            {/* Scrim — click-outside dismiss, invisible. */}
            <div
              className="fixed inset-0 z-30"
              onClick={() => setOpen(false)}
              data-testid="quick-ipcheck-scrim"
            />
            <motion.div
              key="popover"
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-0 top-full z-40 mt-2 w-[280px] rounded-lg border border-white/10 bg-black/90 p-3 shadow-2xl backdrop-blur sm:w-[320px]"
              style={{ boxShadow: "0 10px 40px -8px rgb(var(--accent) / 0.35)" }}
              data-testid="quick-ipcheck-popover"
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute right-1.5 top-1.5 rounded p-0.5 text-text-muted hover:bg-white/5 hover:text-text-main"
                aria-label="Close"
                data-testid="quick-ipcheck-close"
              >
                <X className="h-3 w-3" />
              </button>

              {running && !result && (
                <div className="flex items-center gap-2 py-1 text-xs text-text-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Detecting your IP…
                </div>
              )}

              {error && (
                <div
                  className="space-y-2 py-1 text-xs text-rose-200"
                  data-testid="quick-ipcheck-error"
                >
                  <div>{error.msg}</div>
                  {error.code === "no_client_ip" && onOpenModal && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        onOpenModal(""); // open blank → user types
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-accent/30 bg-accent/10 px-2 py-1 text-[11px] text-accent hover:bg-accent/20"
                      data-testid="quick-ipcheck-error-open-modal"
                    >
                      Open checker <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}

              {result && (
                <div className="space-y-2" data-testid="quick-ipcheck-result">
                  <div className="flex items-start gap-2.5">
                    <PopoverRing score={result.combined_score} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        {result.geo?.flag_emoji && (
                          <span>{result.geo.flag_emoji}</span>
                        )}
                        <span className="truncate font-mono text-xs text-text-main">
                          {result.ip}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-[10px] text-text-muted">
                        {[result.geo?.city, result.geo?.country, result.geo?.isp]
                          .filter(Boolean)
                          .join(" · ") || "no geo"}
                      </div>
                    </div>
                  </div>

                  {/* Duplicate alert — prominent per owner's correction. */}
                  {dup && dup.count > 0 ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        boxShadow: [
                          "0 0 0 0 rgb(251 191 36 / 0.0)",
                          "0 0 0 5px rgb(251 191 36 / 0.18)",
                          "0 0 0 0 rgb(251 191 36 / 0.0)",
                        ],
                      }}
                      transition={{
                        boxShadow: { duration: 1.4, repeat: 2 },
                      }}
                      className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200"
                      data-testid="quick-ipcheck-dupe"
                    >
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold">
                          You've used this IP before · ×{dup.count}
                        </div>
                        <div className="text-amber-200/80">
                          last {new Date(dup.last_checked_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          {" · "}
                          score {Math.round(dup.last_score)} → <span style={{ color: scoreCol }}>{Math.round(result.combined_score)}</span>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="text-[10px] text-emerald-400">
                      First time seeing this IP
                    </div>
                  )}

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1" data-testid="quick-ipcheck-badges">
                    {(result.badges || []).length === 0 ? (
                      <span className="text-[10px] text-text-muted">No fraud signals</span>
                    ) : (
                      result.badges.map((b) => <BadgeChip key={b} b={b} />)
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onOpenModal?.(result.ip);
                    }}
                    className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded-md border border-accent/30 bg-accent/10 px-2 py-1.5 text-[11px] font-medium text-accent hover:bg-accent/20"
                    data-testid="quick-ipcheck-details"
                  >
                    Details <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
